import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { Reveal } from '@/components/generals/reveal'
import { HoverMedia } from '@/components/generals/hover-media'
import { NomaMark } from '@/components/generals/noma-mark'
import { SplitText } from '@/components/generals/split-text'
import { ArrowLink } from '@/components/generals/arrow-link'
import { SectionLabel } from '@/components/generals/section-label'
import { JustifiedSplit } from '@/components/studio/justified-split'
import { media } from '@/constants/media'
import { studioMeta } from '@/constants/site'
import { team } from '@/constants/studio'

/* Film grain, generated rather than fetched: one tiled feTurbulence rect at a
   very low opacity is what stops the flat orange panel reading as plastic. */
const GRAIN = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='240'%20height='240'%3E%3Cfilter%20id='noma-grain'%3E%3CfeTurbulence%20type='fractalNoise'%20baseFrequency='0.86'%20numOctaves='4'%20stitchTiles='stitch'/%3E%3C/filter%3E%3Crect%20width='240'%20height='240'%20filter='url(%23noma-grain)'/%3E%3C/svg%3E"

const GRAIN_STYLE: CSSProperties = {
    backgroundImage: `url("${GRAIN}")`,
    backgroundSize: '240px auto',
    backgroundRepeat: 'repeat',
    opacity: 0.07
}

const HIRING_TITLE = "join us, we're hiring."

const HIRING_COPY = "We're looking for highly ambitious and talented people to help us drive real growth."

export const StudioTeam: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        const grain = root.querySelector<HTMLElement>('[data-team-grain]')

        if (!grain) {
            return
        }

        /* The grain crawls against the scroll, so the panel breathes instead of
           sitting perfectly still behind the cards. */
        gsap.fromTo(grain,
            { backgroundPosition: '0px 0px' },
            {
                backgroundPosition: '0px 480px',
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1
                }
            }
        )
    }, scope, [])

    return (
        <section
            id='studio-team'
            ref={scope}
            className='relative z-10 bg-paper px-[3px] text-ink'
        >
            {/* Flooding the panel in the accent is the template move, and it was
                the loudest thing on the page. It runs on the site's own
                near-black now, with the accent kept back for the statement's two
                emphasised runs and the hover. */}
            <div className='relative overflow-hidden rounded-[var(--radius-frame)] bg-ink py-[42px] text-paper'>
                <div
                    data-team-grain
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0'
                    style={GRAIN_STYLE}
                />

                <div className='relative mx-auto flex max-w-[1920px] flex-col gap-12 px-5 lg:gap-[72px] lg:px-[33px]'>
                    {/* Panel header */}
                    <div className='grid grid-cols-1 items-center gap-5 lg:grid-cols-4'>
                        <div aria-hidden='true' className='hidden lg:block' />

                        <Reveal
                            from='up'
                            distance={22}
                            className='flex items-center gap-2 text-paper'
                        >
                            <NomaMark className='size-[15px]' />

                            <span className='t-display pb-[0.06em] text-[19px] leading-none'>
                                Noma
                            </span>
                        </Reveal>

                        <Reveal
                            from='up'
                            distance={22}
                            delay={0.1}
                            className='flex lg:col-span-2 lg:justify-end'
                        >
                            <span
                                className='flex w-fit items-center gap-2.5 rounded-full border px-3.5 py-2 text-paper'
                                style={{ borderColor: 'rgba(255, 255, 255, 0.5)' }}
                            >
                                <svg
                                    viewBox='0 0 24 24'
                                    className='size-[15px]'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='1.3'
                                    aria-hidden='true'
                                    focusable='false'
                                >
                                    <circle cx='12' cy='12' r='9.2' />
                                    <ellipse cx='12' cy='12' rx='4' ry='9.2' />
                                    <path d='M2.8 12h18.4M4.4 7h15.2M4.4 17h15.2' />
                                </svg>

                                <span className='t-mono'>
                                    {studioMeta.established}
                                </span>
                            </span>
                        </Reveal>
                    </div>

                    {/* Team */}
                    <div className='flex flex-col gap-10 lg:gap-14'>
                        <SectionLabel label='Meet the team' tone='light' />

                        <JustifiedSplit
                            as='h2'
                            runs={[
                                { text: 'WE STAYED' },
                                { text: 'SMALL', className: 'text-paper' },
                                { text: 'SO WE COULD' },
                                { text: 'STAY PICKY.', className: 'text-brand' },
                                { text: 'EVERY PERSON ON A PROJECT IS THERE BECAUSE THE WORK NEEDED THEM, NOT BECAUSE THEY HAPPENED TO BE FREE.' }
                            ]}
                            indent={17}
                            stagger={0.006}
                            start='top 88%'
                            className='t-display text-paper/65'
                            style={{ fontSize: 'clamp(30px, 4.6vw, 60px)' }}
                        />

                        <div className='grid grid-cols-1 gap-x-5 gap-y-8 lg:grid-cols-4'>
                            <div aria-hidden='true' className='hidden lg:block' />

                            <Reveal
                                from='up'
                                distance={18}
                                className='lg:col-span-3'
                            >
                                <span className='t-mono text-paper'>
                                    ({team.length}) Team Members
                                </span>
                            </Reveal>

                            <Reveal
                                from='up'
                                distance={80}
                                duration={1.2}
                                stagger={0.12}
                                childSelector='[data-team-card]'
                                className='col-span-full grid grid-cols-2 gap-x-5 gap-y-8 lg:grid-cols-4'
                            >
                                {team.map((member) => (
                                    <figure
                                        key={member.name}
                                        data-team-card
                                        className='flex flex-col gap-4'
                                    >
                                        <div className='relative'>
                                            <HoverMedia
                                                src={member.image}
                                                alt={`${member.name}, ${member.role} at Noma Studio`}
                                                aspect={0.75}
                                                parallax={4}
                                            />

                                            <span
                                                aria-hidden='true'
                                                className='absolute left-3 top-3 flex size-7 items-center justify-center rounded-[var(--radius-field)] bg-brand text-paper'
                                            >
                                                <NomaMark className='size-[11px]' />
                                            </span>
                                        </div>

                                        <figcaption className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3'>
                                            <span className='t-cond text-[17px] leading-none text-paper'>
                                                {member.name}
                                            </span>

                                            <span className='t-mono text-paper/80'>
                                                {member.role}
                                            </span>
                                        </figcaption>
                                    </figure>
                                ))}
                            </Reveal>
                        </div>
                    </div>

                    {/* Hiring */}
                    <div className='grid grid-cols-1 gap-x-5 gap-y-10 lg:grid-cols-4'>
                        <div className='flex flex-col justify-end gap-6'>
                            <SplitText
                                as='h3'
                                text={HIRING_TITLE}
                                stagger={0.02}
                                className='t-display text-paper'
                                style={{ fontSize: 'clamp(30px, 3.2vw, 40px)' }}
                            />

                            <Reveal
                                from='up'
                                distance={26}
                                delay={0.12}
                                className='flex flex-col gap-6'
                            >
                                <p className='t-cond-soft max-w-[40ch] text-[13px] leading-[1.45] text-paper'>
                                    {HIRING_COPY}
                                </p>

                                <div className='flex items-center gap-3'>
                                    <span className='t-mono rounded-full bg-paper px-2.5 py-1.5 text-ink'>
                                        02
                                    </span>

                                    <span className='t-mono text-paper'>
                                        Open Positions
                                    </span>
                                </div>

                                <ArrowLink
                                    href={`mailto:${studioMeta.email}`}
                                    label='Email us'
                                    external
                                    tone='light'
                                />
                            </Reveal>
                        </div>

                        <div aria-hidden='true' className='hidden lg:block' />

                        <Reveal
                            from='right'
                            distance={70}
                            duration={1.3}
                            className='lg:col-span-2'
                        >
                            <HoverMedia
                                src={media.teamPhoto}
                                alt='The Noma Studio team together outside the studio'
                                aspect={1.43}
                                parallax={12}
                            />
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}
